
import { defineComponent, inject, PropType } from 'vue'
import TextArea from '@/components/shared/TextArea.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import CreatePurchaseOrderItemModal from '@/components/modals/CreatePurchaseOrderItemModal.vue'
import NoteModal from '@/components/modals/NoteModal.vue'
import NoteTile from '@/components/elements/NoteTile.vue'
import { PurchaseOrderItemDetails } from '@/@types/responses/purchase_order/PurchaseOrderItemDetails'
import { PurchaseOrderDetails } from '@/@types/responses/purchase_order/PurchaseOrderDetails'
import { Note } from '@/@types/responses/note/Note'
import { PurchaseOrderService } from '@/services/purchaseOrder.service'
import { NoteService } from '@/services/note.service'
import { PurchaseOrderItemService } from '@/services/purchaseOrderItem.service'
import Dropdown from '@/components/shared/Dropdown.vue'
import { SupplierService } from '@/services/supplier.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { PurchaseOrderItemForDetails } from '@/@types/responses/purchase_order/PurchaseOrderItemForDetails'
import { InventoryItemService } from '@/services/inventoryItem.service'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { UpdatePurchaseOrderMessage } from '@/@types/messages/UpdatePurchaseOrderMessage'
import { CreatePurchaseOrderMessage } from '@/@types/messages/CreatePurchaseOrderMessage'
import { UpdatePurchaseOrderItemMessage } from '@/@types/messages/UpdatePurchaseOrderItemMessage'
import { CustomerService } from '@/services/customer.service'
import { CustomerDetails } from '@/@types/responses/customer/CustomerDetails'
import Checkbox from '@/components/shared/Checkbox.vue'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'PurchaseOrder',
  components: {
    AuditInformation,
    Checkbox,
    CreatePurchaseOrderItemModal,
    Dropdown,
    LoadingPulse,
    LockingButton,
    NoteModal,
    NoteTile,
    TextArea,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showCreatePurchaseOrderItemModal || this.showNoteModal) {
      this.showCreatePurchaseOrderItemModal = false
      this.showNoteModal = false

      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    },
    customerId: {
      type: Number,
      default: 0
    },
    orderId: {
      type: Number,
      default: 0
    },
    orderPartIds: {
      type: Array as PropType<Array<number>>,
      default: []
    },
    orderPartName: {
      type: String,
      default: ''
    },
    orderPartPrice: {
      type: Number,
      default: 0
    }
  },
  setup() {
    const customerService = inject(PROVIDER_CONSTANTS.customerService) as CustomerService
    const inventoryItemService = inject(PROVIDER_CONSTANTS.inventoryItemService) as InventoryItemService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const purchaseOrderItemService = inject(PROVIDER_CONSTANTS.purchaseOrderItemService) as PurchaseOrderItemService
    const purchaseOrderService = inject(PROVIDER_CONSTANTS.purchaseOrderService) as PurchaseOrderService
    const supplierService = inject(PROVIDER_CONSTANTS.supplierService) as SupplierService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      customerService,
      inventoryItemService,
      noteService,
      purchaseOrderItemService,
      purchaseOrderService,
      supplierService,
      toast
    }
  },
  data() {
    let customer: CustomerDetails|undefined
    let noteToEdit: Note|undefined
    let purchaseOrder: PurchaseOrderDetails|undefined
    let unchangedPurchaseOrder: PurchaseOrderDetails|undefined

    return {
      customer,
      loading: true,
      noteToEdit,
      purchaseOrder,
      saving: false,
      savingStateChange: false,
      showCreatePurchaseOrderItemModal: false,
      showNoteModal: false,
      supplierDropdownOptions: [] as DropdownOptionResponse<number>[],
      unchangedPurchaseOrder
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.purchaseOrder
      const b = this.unchangedPurchaseOrder

      return (a?.description !== b?.description ||
              a?.venderPoNumber !== b?.venderPoNumber ||
              a?.supplierId !== b?.supplierId ||
              a?.setACalendarDate !== b?.setACalendarDate) && 
              a?.supplierId !== undefined
    },
    supplierName(): string {
      if (!this.purchaseOrder) return ''

      const supplierId = this.purchaseOrder.supplierId
      const index = this.supplierDropdownOptions.findIndex(s => s.id === supplierId)
      if (index === -1) return ''

      return this.supplierDropdownOptions[index].name
    },
    titleText(): string {
      return !this.purchaseOrder || this.purchaseOrder.id === 0 ? 'Create Purchase Order' : 'Edit Purchase Order'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    if (this.id > 0) {
      this.getSupplierDropdownOptions().then(() => {
        this.getPurchaseOrder()
      })
    } else {
      Promise.all([this.getCustomer(), this.getSupplierDropdownOptions()]).then(() => {
        this.purchaseOrder = new PurchaseOrderDetails(0)
        this.unchangedPurchaseOrder = new PurchaseOrderDetails(0)

        if (this.customerId > 0 && this.orderPartIds.length > 0 && this.customer) {
          const purchaseOrderItemFor = new PurchaseOrderItemForDetails(`${this.customer.firstName} ${this.customer.lastName}`, this.orderId)
          const purchaseOrderItems = [] as PurchaseOrderItemDetails[]
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          for (const _ of this.orderPartIds) {
            purchaseOrderItems.push(new PurchaseOrderItemDetails(0, false, 0, this.orderPartName, this.orderPartPrice, purchaseOrderItemFor, undefined, undefined))
          }

          this.purchaseOrder.purchaseOrderItems = purchaseOrderItems
        }

        this.loading = false
      })
    }
  },
  methods: {
    getCustomer(): Promise<void> {
      if (this.customerId === 0) return Promise.resolve()

      return this.customerService.getCustomer(this.customerId).then(customer => {
        this.customer = customer
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the customer. Try reloading the page.')
        }
      })
    },
    getPurchaseOrder(): Promise<void> {
      return this.purchaseOrderService.getPurchaseOrder(this.purchaseOrder ? this.purchaseOrder.id : this.id).then(purchaseOrder => {
        this.purchaseOrder = purchaseOrder
        this.unchangedPurchaseOrder = JSON.parse(JSON.stringify(purchaseOrder))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false
        this.savingStateChange = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the purchase order. Try reloading the page.')
        }
      })
    },
    getSupplierDropdownOptions(): Promise<void> {
      return this.supplierService.getSupplierDropdownOptions().then(dropdownOptionsResponse => {
        this.supplierDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the suppliers. Try reloading the page.')
        }
      })
    },
    onAddItemClicked(): void {
      this.showCreatePurchaseOrderItemModal = true
    },
    onCreateNoteClicked(): void {
      this.noteToEdit = new Note(0)
      this.showNoteModal = true
    },
    onDeleteNote(note: Note): void {
      this.noteService.deleteNote(note.id).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('The note was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the note. Please try again.')
        }
      })
    },
    onDeletePurchaseOrderItemClicked(purchaseOrderItemId: number): void {
      this.purchaseOrderItemService.deletePurchaseOrderItem(purchaseOrderItemId).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('The purchase order item was removed successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to remove the purchase order item. Please try again.')
        }
      })
    },
    onEditNote(note: Note): void {
      this.noteToEdit = note
      this.showNoteModal = true
    },
    onPurchaseOrderItemChanged(purchaseOrderItem: PurchaseOrderItemDetails): void {
      const updatePurchaseOrderItemMessage = new UpdatePurchaseOrderItemMessage(purchaseOrderItem.canBeUsedToFulfillOrderPart, false)
      this.purchaseOrderItemService.updatePurchaseOrderItem(purchaseOrderItem.id, updatePurchaseOrderItemMessage).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('Purchase order item updated successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to update the purchase order item. Please try again.')
        }
      })
    },
    onReceiveAllPurchaseOrderItemsClicked(): void {
      this.savingStateChange = true
      this.purchaseOrderService.receiveAllPurchaseOrderItems(this.purchaseOrder ? this.purchaseOrder.id : this.id).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('All of the purchase order items were received successfully.')
          this.savingStateChange = false
        })
      }).catch(error => {
        this.savingStateChange = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to receive all of the purchase order items. Please try again.')
        }
      })
    },
    onReceivePurchaseOrderItemClicked(purchaseOrderItem: PurchaseOrderItemDetails): void {
      const updatePurchaseOrderItemMessage = new UpdatePurchaseOrderItemMessage(purchaseOrderItem.canBeUsedToFulfillOrderPart, true)
      this.purchaseOrderItemService.updatePurchaseOrderItem(purchaseOrderItem.id, updatePurchaseOrderItemMessage).then(() => {
        this.getPurchaseOrder().then(() => {
          this.toast.success('Purchase order item received successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to receive the purchase order item. Please try again.')
        }
      })
    },
    onSavePurchaseOrderClicked(savingStateChange: boolean): void {
      if (!this.purchaseOrder || !this.purchaseOrder.supplierId) return

      if (savingStateChange)
        this.savingStateChange = true
      else
        this.saving = true

      if (this.purchaseOrder.id === 0) {
        let customerId: number|undefined
        if (this.customerId > 0)
          customerId = this.customerId

        const createPurchaseOrderMessage = new CreatePurchaseOrderMessage(customerId, this.purchaseOrder.description, this.purchaseOrder.setACalendarDate, this.purchaseOrder.supplierId, this.purchaseOrder.venderPoNumber, this.orderPartIds)
        this.purchaseOrderService.createPurchaseOrder(createPurchaseOrderMessage).then(objectCreatedResponse => {
          if (!this.purchaseOrder) return

          this.purchaseOrder.id = objectCreatedResponse.id
          this.getPurchaseOrder().then(() => {
            this.$router.replace(`/purchase-order/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The purchase order was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the purchase order. Please try again.')
          }
        })
      } else {
        const updatePurchaseOrderMessage = new UpdatePurchaseOrderMessage(this.purchaseOrder.description, this.purchaseOrder.setACalendarDate, this.purchaseOrder.venderPoNumber, this.purchaseOrder.submitted, this.purchaseOrder.verified)
        this.purchaseOrderService.updatePurchaseOrder(this.id, updatePurchaseOrderMessage).then(() => {
          this.getPurchaseOrder().then(() => {
            this.toast.success('The purchase order was updated successfully.')
            this.saving = false
            this.savingStateChange = false
          })
        }).catch(error => {
          this.saving = false
          this.savingStateChange = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the purchase order. Please try again.')
          }
        })
      }
    },
    onSubmitPurchaseOrderClicked(): void {
      if (!this.purchaseOrder) return

      this.purchaseOrder.submitted = true
      this.onSavePurchaseOrderClicked(true)
    },
    onVerifyPurchaseOrderClicked(): void {
      if (!this.purchaseOrder) return

      this.purchaseOrder.verified = true
      this.onSavePurchaseOrderClicked(true)
    },
    receivedText(purchaseOrderItem: PurchaseOrderItemDetails): string {
      if (!purchaseOrderItem.receivedByUser) return ''

      return `Received by ${purchaseOrderItem.receivedByUser.firstName} ${purchaseOrderItem.receivedByUser.lastName}`
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.purchaseOrder)
        document.title = `PO #${this.purchaseOrder.poNumber} - Short Action Customs, LLC`
    }
  }
})
